import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import DealHistory from "./DealHistory";
import DealSummarySections from "./DealSummarySections";
import { apiFetch } from "../../utils/apiFetch";
import "./global/global.css";

const DealDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const [deal, setDeal] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [dealHistory, setDealHistory] = useState({
    emails: [],
    documents: [],
    timeline: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/deals/${id}/`
        );
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Deal not found");
          }
          throw new Error("Failed to fetch deal details");
        }
        const data = await response.json();
        setDeal(data.deal);
        setContacts(data.contacts || []);
        setMilestones(data.milestones || []);
        setDealHistory({
          emails: data.emails || [],
          documents: data.documents || [],
          timeline: data.timeline || [],
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [id]);

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this deal? This will stop all scheduled reminders and cannot be undone from this page."
      )
    ) {
      return;
    }

    setCancelling(true);
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/deals/${id}/cancel/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to cancel deal");
      }
      const data = await response.json();
      setDeal((prev) => ({
        ...prev,
        status: data.deal.status,
        program_status: data.deal.program_status,
      }));
      setMilestones((prev) =>
        prev.map((m) =>
          m.status === "pending" || m.status === "in_progress"
            ? { ...m, status: "skipped" }
            : m
        )
      );
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const canCancel =
    deal && deal.status !== "closed" && deal.status !== "cancelled";

  if (loading) {
    return (
      <div className="deal-details-root">
        <div className="deal-details-container">
          <p className="loading">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="deal-details-root">
        <div className="deal-details-container">
          <div className="deal-details-header">
            <button
              className="deal-details-back"
              onClick={() => history.push("/client-list")}
            >
              &larr; Back to Deals
            </button>
          </div>
          <p className="error">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deal-details-root">
      <div className="deal-details-container">
        <div className="deal-details-header">
          <button
            className="deal-details-back"
            onClick={() => history.push("/client-list")}
          >
            &larr; Back to Deals
          </button>
          <div className="deal-details-header-right">
            {canCancel && (
              <button
                className="deal-details-cancel-btn"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Cancel Deal"}
              </button>
            )}
          </div>
        </div>

        <DealSummarySections
          deal={deal}
          contacts={contacts}
          milestones={milestones}
        />
        <DealHistory
          dealId={id}
          emails={dealHistory.emails}
          documents={dealHistory.documents}
          timeline={dealHistory.timeline}
        />
      </div>
    </div>
  );
};

export default DealDetails;
